import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Form, Select, Spin, message } from 'antd';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  FemaleIcon,
  MaleIcon,
  VoicePauseIcon,
  VoicePlayIcon,
} from '@/assets/icons/icons';
import type { ApiVoice, VoiceOption, VoiceOptionValue } from '../types';
import SearchInput from '@/components/SearchInput';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import { GetAiAgentVoicesWithSearchAndPagination } from '@/apis/settings';

const mapApiVoiceToOption = (v: ApiVoice): VoiceOption => ({
  value: String(v.id),
  label: `${v.accent}, ${v.age}`,
  gender: v.gender,
  preview_location: v.preview_location,
  order: v.order,
});

export const VoiceSection: React.FC<{
  loadingDropdowns: boolean;
  playingVoice: VoiceOptionValue | null;
  onToggleVoice: (v: VoiceOptionValue, previewUrl?: string) => void;
  disabled?: boolean;
  presetVoice?: VoiceOption | null;
}> = ({
  loadingDropdowns,
  playingVoice,
  onToggleVoice,
  disabled = false,
  presetVoice = null,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();

  const [voicesList, setVoicesList] = useState<VoiceOption[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [dropdownLoading, setDropdownLoading] = useState<boolean>(false);
  const [voiceSelectOpen, setVoiceSelectOpen] = useState<boolean>(false);

  const dropdownScrollElRef = useRef<HTMLDivElement | null>(null);
  const sortByOrder = (list: VoiceOption[]): VoiceOption[] => {
    const toNum = (v: number | string | null | undefined): number => {
      if (v === null || v === undefined) return Number.POSITIVE_INFINITY;
      const n = Number(v);
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    };

    return [...list].sort((a, b) => {
      const ao = toNum(a.order);
      const bo = toNum(b.order);

      if (ao !== bo) return ao - bo;
      return String(a.label ?? '').localeCompare(String(b.label ?? ''));
    });
  };

  useEffect(() => {
    if (!presetVoice?.value) return;

    setVoicesList((prev) => {
      const id = String(presetVoice.value);
      const exists = prev.some((x) => String(x.value) === id);
      const merged = exists ? prev : [...prev, presetVoice];
      return sortByOrder(merged);
    });
  }, [presetVoice]);

  const fetchVoices = useCallback(
    (pageNum: number, q: string) => {
      if (!token) return;
      setDropdownLoading(true);

      GetAiAgentVoicesWithSearchAndPagination(token, q, pageNum)
        .then((res) => {
          const meta = res?.data?.data;

          if (meta?.is_success) {
            const rawList: ApiVoice[] = meta?.data || [];

            const mapped = rawList
              .filter((v) => !v.is_archived)
              .map(mapApiVoiceToOption);

            setVoicesList((prev) => {
              const seen = new Set(prev.map((x) => String(x.value)));
              const next = [...prev];
              for (const item of mapped) {
                const k = String(item.value);
                if (!seen.has(k)) {
                  seen.add(k);
                  next.push(item);
                }
              }
              return sortByOrder(next);
            });

            setTotalPages(meta?.page_count ?? 1);
            setPage(pageNum);
          } else {
            message.error(meta?.message || 'Failed to fetch voice options');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch voice options');
        })
        .finally(() => setDropdownLoading(false));
    },
    [token, unauthorize]
  );

  const fetchMore = useCallback(() => {
    if (!token) return;
    if (dropdownLoading || page >= totalPages) return;

    const nextPage = page + 1;
    setDropdownLoading(true);

    GetAiAgentVoicesWithSearchAndPagination(token, search, nextPage)
      .then((res) => {
        const meta = res?.data?.data;
        if (meta?.is_success) {
          const rawList: ApiVoice[] = meta?.data || [];

          const mapped = rawList
            .filter((v) => !v.is_archived)
            .map(mapApiVoiceToOption);

          setVoicesList((prev) => {
            const seen = new Set(prev.map((x) => String(x.value)));
            const next = [...prev];
            for (const item of mapped) {
              const k = String(item.value);
              if (!seen.has(k)) {
                seen.add(k);
                next.push(item);
              }
            }
            return next;
          });

          setPage(nextPage);
        } else {
          message.error(meta?.message || 'Failed to load more voices');
        }
      })
      .catch((error) => {
        unauthorize(error);
        message.error('Failed to load more voices');
      })
      .finally(() => {
        setDropdownLoading(false);
      });
  }, [token, dropdownLoading, page, totalPages, search, unauthorize]);

  const firstRunRef = useRef(true);
  useEffect(() => {
    if (!token) return;
    if (disabled) return;

    if (firstRunRef.current) {
      firstRunRef.current = false;
      fetchVoices(1, search);
      return;
    }

    const t = setTimeout(() => {
      fetchVoices(1, search);
    }, 400);

    return () => clearTimeout(t);
  }, [token, search, fetchVoices, disabled]);

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      dropdownScrollElRef.current = target;

      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (nearBottom) fetchMore();
    },
    [fetchMore]
  );

  const renderVoiceSelectedLabel = (option: VoiceOption) => {
    const isPlaying = playingVoice === option.value;
    const serial = option.order ?? '';

    return (
      <div className='voiceSelected'>
        <span className='voiceSerial'>{serial}</span>
        <span className='voiceSelected__gender'>
          {option.gender === 'male' ? <MaleIcon /> : <FemaleIcon />}
        </span>
        <span className='voiceSelected__label'>{option.label}</span>
        <span
          className='voiceSelected__play'
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            onToggleVoice(
              option.value as VoiceOptionValue,
              option.preview_location ?? undefined
            );
          }}
        >
          {isPlaying ? <VoicePauseIcon /> : <VoicePlayIcon />}
        </span>
      </div>
    );
  };

  const renderVoiceOption = (option: VoiceOption) => {
    const isPlaying = playingVoice === option.value;

    return (
      <div className='voiceOption'>
        <span className='voiceSerial'>{option.order ?? ''}</span>
        <span className='voiceOption__gender'>
          {option.gender === 'male' ? <MaleIcon /> : <FemaleIcon />}
        </span>
        <span className='voiceOption__label'>{option.label}</span>
        <span
          className='voiceOption__play'
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            onToggleVoice(
              option.value as VoiceOptionValue,
              option.preview_location ?? undefined
            );
          }}
        >
          {isPlaying ? <VoicePauseIcon /> : <VoicePlayIcon />}
        </span>
      </div>
    );
  };

  return (
    <Col span={24} style={{ padding: 0 }}>
      <Form.Item
        label='Voice options'
        name='voice'
        rules={disabled ? [] : [{ required: true, message: 'Select a voice' }]}
      >
        <Select
          disabled={disabled}
          className='agentSelect voiceSelect'
          popupClassName='voiceSelectDropdown'
          placeholder={loadingDropdowns ? 'Loading...' : 'Select...'}
          optionLabelProp='label'
          filterOption={false}
          onPopupScroll={handlePopupScroll}
          onDropdownVisibleChange={setVoiceSelectOpen}
          suffixIcon={
            voiceSelectOpen ? (
              <ChevronUpIcon size={16} />
            ) : (
              <ChevronDownIcon size={16} />
            )
          }
          dropdownRender={(menu) => (
            <>
              <div className='agentVoiceDropdownHeader'>
                <SearchInput
                  placeholder='Search voice'
                  width='100%'
                  onChange={(val) => setSearch(val)}
                  onClear={() => setSearch('')}
                />
              </div>
              {menu}
              {dropdownLoading && (
                <div className='agentVoiceDropdownLoading'>
                  <Spin size='small' />
                </div>
              )}
            </>
          )}
        >
          {voicesList.map((opt) => (
            <Select.Option
              key={String(opt.value)}
              value={opt.value}
              label={renderVoiceSelectedLabel(opt)}
            >
              {renderVoiceOption(opt)}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </Col>
  );
};
